import dedent from 'ts-dedent'
import {
  ColumnDataType,
  Language,
  Provider,
  QueryAndOutputs,
  WorkflowCodeType,
  WorkflowComponentCodeParams,
  WorkflowComponentParamType
} from '../types'
import { createTableWithBBox } from '../utils/databricks'
import { DBricksConnection } from '../workflow/connections/databricks'
import { splitPairs } from './common/enrichcomponent'
import {
  SINGLE_OUTPUT_NAME,
  SingleOutputWorkflowComponent
} from './common/singleoutputcomponent'

class EnrichPointsDatabricks extends SingleOutputWorkflowComponent {
  constructor() {
    super(
      {
        name: 'native.enrichpoints',
        group: 'Data Enrichment',
        title: 'Enrich Points',
        description: `
      This uses a data source to enrich another target table with points.
      `,
        starred: true
      },
      [
        {
          name: 'target',
          type: WorkflowComponentParamType.InputTable,
          title: 'Target points to be enriched',
          placeholder: 'FQN of the target table'
        },
        {
          name: 'source',
          type: WorkflowComponentParamType.InputTable,
          title: 'Source table',
          description: 'Source for the enrichment',
          placeholder: 'FQN of the source table'
        },
        {
          name: 'target_col',
          type: WorkflowComponentParamType.Column,
          title: 'Target point geo column',
          parent: 'target',
          dataType: [ColumnDataType.Geography, ColumnDataType.Geometry]
        },
        {
          name: 'source_col',
          type: WorkflowComponentParamType.Column,
          title: 'Source geo column',
          parent: 'source',
          dataType: [ColumnDataType.Geography, ColumnDataType.Geometry],
          optional: false
        },
        {
          name: 'variables',
          type: WorkflowComponentParamType.SelectColumnAggregation,
          title: 'Variables',
          parent: 'source',
          noDefault: true,
          doVariables: true,
          description:
            'Comma-separated list of variable names and aggregation methods (e.g. population_93405ad7, sum, population_93405ad7, avg)',
          placeholder: 'population_93405ad7, sum, population_93405ad7, avg',
          optional: false
        }
      ]
    )
    this.supportedProviders = [Provider.DatabricksRest]
  }

  async getCode({
    workflow,
    params,
    connection,
    componentName,
    codeType
  }: WorkflowComponentCodeParams): Promise<QueryAndOutputs | null> {
    const dryRun = codeType === WorkflowCodeType.DryRun
    const outputTablename = connection.getOutputTableName(
      workflow.id,
      componentName,
      params,
      SINGLE_OUTPUT_NAME,
      codeType
    )
    const preparedParams = this._prepareParameters(params)
    const variables = preparedParams.variables as string
    const variablePairs = splitPairs(
      variables?.split(',')?.map((x) => x?.trim())
    )
    let query = ''
    if (dryRun) {
      const selectQuery = dedent`
        SELECT
          *,
          ${variablePairs
            .map(([variable, aggregation]) => `0 AS ${variable}_${aggregation}`)
            .join(',\n')}
        FROM
        ${connection.quoteName(preparedParams.target as string)}`
      query = connection.getCreateTempTableQuery(
        outputTablename,
        selectQuery,
        codeType,
        null
      )
      query = (connection as DBricksConnection).wrapSQLCodeAsPythonCall(query)
    } else {
      const processedSourceTablename = connection.quoteName(
        outputTablename + '_sourceprocessed'
      )
      const processedTargetTablename = connection.quoteName(
        outputTablename + '_targetprocessed'
      )
      const createSourceTableQuery = createTableWithBBox(
        connection as DBricksConnection,
        preparedParams.source as string,
        processedSourceTablename,
        preparedParams.source_col as string,
        (x) => `ST_GEOMFROMWKB(${x})`
      )
      const createTargetTableQuery = createTableWithBBox(
        connection as DBricksConnection,
        preparedParams.target as string,
        processedTargetTablename,
        preparedParams.target_col as string,
        (x) => `ST_GEOMFROMWKB(${x})`
      )

      const select = dedent`
        WITH intersection AS (
          SELECT /*+ REPARTITION(40, ${preparedParams.source_col}) */
            target.${preparedParams.target_col} AS geom,
            ${variablePairs
              .map(
                ([variable, aggregation]) =>
                  `${aggregation}(source.${variable}) AS ${variable}_${aggregation}`
              )
              .join(',\n')}
          FROM
            ${processedTargetTablename} AS target,
            ${processedSourceTablename} AS source
          WHERE
            (
              target.xmax >= source.xmin
              AND target.xmin <= source.xmax
              AND target.ymax >= source.ymin
              AND target.ymin <= source.ymax
            )
          AND
            ST_INTERSECTS(
              ST_GEOMFROMWKB(target.${preparedParams.target_col}),
              ST_GEOMFROMWKB(source.${preparedParams.source_col})
            )
          GROUP BY
            target.${preparedParams.target_col}
        )
        SELECT
          target.*,
          ${variablePairs
            .map(
              ([variable, aggregation]) =>
                `intersection.${variable}_${aggregation}`
            )
            .join(',\n')}
        FROM
          ${processedTargetTablename} AS target
        LEFT JOIN
          intersection
        ON
          target.${preparedParams.target_col} = intersection.geom
        `

      query = connection.getCreateTempTableQuery(
        outputTablename,
        select,
        codeType,
        null
      )
      query = dedent`
        ${createSourceTableQuery}
        ${createTargetTableQuery}
        ${(connection as DBricksConnection).wrapSQLCodeAsPythonCall(query)}`
    }

    return {
      query,
      outputs: { result: outputTablename }
    }
  }

  language(provider: Provider): Language {
    return Language.Python
  }

  getUIProperties(provider?: Provider): Record<string, unknown> {
    const classifiedMethods: Record<string, Array<string>> = {}
    classifiedMethods[ColumnDataType.Number] = [
      'min',
      'max',
      'avg',
      'sum',
      'count'
    ]
    return {
      methods: classifiedMethods,
      allowedTypes: [ColumnDataType.Number],
      excludedNames: ['geoid', 'geom']
    }
  }
}

export { EnrichPointsDatabricks }
